import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { format, addDays } from 'date-fns';
import get from 'lodash.get';
import {
  Box,
  Button,
  ColorPicker,
  FlexBox,
  FormDialog,
  Grid,
  InputField,
  MuiBox,
  SelectDropdown,
  Switch,
  Typography
} from '@kis-technologies/kis-ui';
import { FONTS, Font } from 'constants/fonts';
import { useActions } from 'hooks/useActions';
import { actions as clientActions, selectors as clientsSelectors, Client, SalesReportSettings } from 'store/clients';
import {
  actions as configActions,
  selectors as configSelectors,
  Configuration,
  UpdateConfigurationPayload
} from 'store/configurations';
import { KioskSalesByCardType } from 'store/deliveryLog';
import { actions as settingsActions, selectors as settingsSelectors } from 'store/settings';
import {
  exportKioskSalesReport,
  kioskSalesToKioskSalesByCardType,
  salesReportSettingsToSFOTSalesReportSettings
} from 'utils/sfotKioskSalesReport';
import { doesFormHaveInitialValues, getUpdatedResourceWithFormValues, onValueChange } from 'utils/forms';
import { getSortedList } from 'utils/stringFormat';
import { useStyles } from './styled';
import { ConfigurationForm, SettingsViewProps } from './types';
import { getConfigurationForm } from './utils';

const SettingsView = ({ setHasUnsavedChanges }: SettingsViewProps) => {
  const classes = useStyles();

  // ACTIONS AND SELECTORS
  const {
    resetClientAction,
    invalidateCache,
    getConfigurations,
    updateConfiguration,
    resetConfigurationsAction,
    getSalesByKiosk,
    setConfigurationId,
    resetSettingsAction,
    resetSalesByKiosk
  } = useActions({
    resetClientAction: clientActions.resetAction,
    invalidateCache: clientActions.invalidateCache,
    getConfigurations: configActions.getConfigurations,
    updateConfiguration: configActions.updateConfiguration,
    resetConfigurationsAction: configActions.resetAction,
    getSalesByKiosk: settingsActions.getSalesByKiosk,
    setConfigurationId: settingsActions.setConfigurationId,
    resetSettingsAction: settingsActions.resetAction,
    resetSalesByKiosk: settingsActions.resetSalesByKiosk
  });
  const client = useSelector(clientsSelectors.selectClient) as Client;
  const clientAction = useSelector(clientsSelectors.selectAction);
  const configurations = useSelector(configSelectors.selectConfigurations);
  const configurationId = useSelector(settingsSelectors.selectConfigurationId);
  const configurationsAction = useSelector(configSelectors.selectAction);
  const salesByKiosk = useSelector(settingsSelectors.selectSalesByKiosk);
  const settingsAction = useSelector(settingsSelectors.selectAction);

  const initialForm = useMemo(() => getConfigurationForm(), []);
  const [salesReportDate, setSalesReportDate] = useState<string>(format(new Date(), 'MM-dd-yyyy'));
  const [isSalesReportDateValid, setIsSalesReportDateValid] = useState<boolean>(true);
  const [salesReportStatus, setSalesReportStatus] = useState<string>('');
  const [salesReportDayOfEvent, setSalesReportDayOfEvent] = useState<number>(0);
  const [salesReportCurrentYearDate, setSalesReportCurrentYearDate] = useState<string>('');
  const [salesReportPreviousYearDate, setSalesReportPreviousYearDate] = useState<string>('');
  const [oneDayCurrentYearSalesByKiosk, setOneDayCurrentYearSalesByKiosk] = useState<
    KioskSalesByCardType[] | undefined
  >();
  const [currentYearSalesByKiosk, setCurrentYearSalesByKiosk] = useState<KioskSalesByCardType[] | undefined>();
  const [oneDayPreviousYearSalesByKiosk, setOneDayPreviousYearSalesByKiosk] = useState<
    KioskSalesByCardType[] | undefined
  >();
  const [previousYearSalesByKiosk, setPreviousYearSalesByKiosk] = useState<KioskSalesByCardType[] | undefined>();
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [selectedConfiguration, setSelectedConfiguration] = useState<Configuration | null>(null);
  const [configurationForm, setConfigurationForm] = useState<ConfigurationForm>(initialForm);
  const [resetForm, setResetForm] = useState<ConfigurationForm>(initialForm);
  const [isDisabled, setIsDisabled] = useState(false);

  const hasInitialValues = useMemo(() => doesFormHaveInitialValues(configurationForm), [configurationForm]);

  const salesReportSettings: SalesReportSettings = get(client, 'configuration.salesReportSettings', {});
  const restrictedFonts: string[] = get(client, 'configuration.restrictedFonts', []);

  const filteredFonts: Font[] = React.useMemo(() => {
    const availableFonts = restrictedFonts.includes('*')
      ? FONTS
      : FONTS.filter((font) => !font.isRestricted || restrictedFonts.includes(font.code));

    return getSortedList(availableFonts, 'displayName');
  }, [restrictedFonts]);

  useEffect(() => {
    getConfigurations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  useEffect(() => {
    if (configurations.length && !configurationId) {
      setConfigurationId(configurations[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configurations]);

  useEffect(() => {
    let configuration = null;
    if (configurationId && configurations.length) {
      configuration = configurations.find((c) => c.id === configurationId);
      if (!configuration) configuration = configurations[0];
    }
    setSelectedConfiguration(configuration);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configurationId, configurations]);

  useEffect(() => {
    if (selectedConfiguration) {
      const form = getConfigurationForm(selectedConfiguration);
      setConfigurationForm(form);
      setResetForm(form);
    }
  }, [selectedConfiguration]);

  useEffect(() => {
    const isEqual = JSON.stringify(configurationForm) === JSON.stringify(resetForm);
    setIsDisabled(isEqual);
    setHasUnsavedChanges(!isEqual);
  }, [configurationForm, resetForm]);

  useEffect(() => {
    if (configurationsAction.success) {
      resetConfigurationsAction();
    }
  }, [configurationsAction.success]);

  /**
   * This triggers actions to build the kiosk sales report for SFT.
   *
   * setTimeout() is used to ensure that the state updates are
   * completed before the next action is triggered.
   */
  useEffect(() => {
    if (salesByKiosk && salesReportStatus === 'oneDayCurrentYear') {
      // Save the response for the 1st date range (out of 4)
      setOneDayCurrentYearSalesByKiosk(kioskSalesToKioskSalesByCardType(salesByKiosk));
      setSalesReportStatus('oneDayPreviousYear');
      resetSalesByKiosk();
      // Get sales for the 2nd date range (out of 4)
      setTimeout(() => {
        getSalesByKiosk({
          startDate: salesReportPreviousYearDate,
          endDate: salesReportPreviousYearDate
        });
      }, 0);
    } else if (salesByKiosk && salesReportStatus === 'oneDayPreviousYear') {
      // Save the response for the 2nd date range (out of 4)
      setOneDayPreviousYearSalesByKiosk(kioskSalesToKioskSalesByCardType(salesByKiosk));
      setSalesReportStatus('currentYear');
      resetSalesByKiosk();
      // Get sales for the 3rd date range (out of 4)
      setTimeout(() => {
        getSalesByKiosk({
          startDate: salesReportSettings.currentEventStartDate,
          endDate: salesReportCurrentYearDate
        });
      }, 0);
    } else if (salesByKiosk && salesReportStatus === 'currentYear') {
      // Save the response for the 3rd date range (out of 4)
      setCurrentYearSalesByKiosk(kioskSalesToKioskSalesByCardType(salesByKiosk));
      setSalesReportStatus('previousYear');
      resetSalesByKiosk();
      // Get sales for the 4th date range (out of 4)
      setTimeout(() => {
        getSalesByKiosk({
          startDate: salesReportSettings.previousEventStartDate,
          endDate: salesReportPreviousYearDate
        });
      }, 0);
    } else if (salesByKiosk && salesReportStatus === 'previousYear') {
      // Save the response for the 4th date range (out of 4)
      setPreviousYearSalesByKiosk(kioskSalesToKioskSalesByCardType(salesByKiosk));
      setSalesReportStatus('exportData');
      setTimeout(() => resetSalesByKiosk(), 0);
    } else if (salesReportStatus === 'exportData') {
      setSalesReportStatus('');
      // Generate the xls file and download
      exportKioskSalesReport({
        sfotSalesReportSettings: salesReportSettingsToSFOTSalesReportSettings(salesReportSettings),
        salesReportCurrentYearDate,
        salesReportPreviousYearDate,
        salesReportDayOfEvent,
        oneDayCurrentYearSalesByKiosk,
        oneDayPreviousYearSalesByKiosk,
        currentYearSalesByKiosk,
        previousYearSalesByKiosk
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesByKiosk]);

  // Handle API errors
  useEffect(() => {
    if (clientAction.error) {
      setErrorMessage(clientAction.error);
      resetClientAction();
    } else if (configurationsAction.error) {
      setErrorMessage('Uh oh! Something went wrong. Please try again.');
      resetConfigurationsAction();
    } else if (settingsAction.error) {
      setErrorMessage(settingsAction.error);
      resetSettingsAction();
      setSalesReportStatus('');
    }
  }, [clientAction.error, configurationsAction.error, settingsAction.error]);

  const isSavable = () => {
    return configurationForm.primaryColor.value && configurationForm.primaryFont.value;
  };

  // Validate and update the sales report date when the input value changes
  const onSalesReportDateInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();

    const value = e.target.value.trim();

    // Validate the format (MM-DD-YYYY)
    const dateRegex = /^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])-(\d{4})$/;
    const isValidDate = dateRegex.test(value);

    // Set the input value and the valid status that enables/disables the button
    setSalesReportDate(value);
    setIsSalesReportDateValid(isValidDate);
  };

  // Start generating the sales report, triggering a useEffect() to complete it
  const onBuildReportClick = () => {
    const { currentEventStartDate, previousEventStartDate } = salesReportSettings;

    try {
      // Reformat the input date
      const currentYearReportDate = format(new Date(salesReportDate), 'yyyy-MM-dd');
      setSalesReportCurrentYearDate(currentYearReportDate);

      // Get the difference (in days) from the event start date that is configured
      const date1 = new Date(`${currentEventStartDate}T00:00:00`);
      const date2 = new Date(`${currentYearReportDate}T00:00:00`);
      const diffInTime = date2.getTime() - date1.getTime();
      const diffInDays = Math.round(diffInTime / (1000 * 3600 * 24));

      if (diffInDays < 0) {
        setErrorMessage(`${salesReportDate} is not a valid date for this report`);
      } else {
        // Use the difference to determine the date of the previous year
        const previousYearSameDayOfEvent = addDays(new Date(`${previousEventStartDate}T00:00:00`), diffInDays);
        setSalesReportPreviousYearDate(format(previousYearSameDayOfEvent, 'yyyy-MM-dd'));
        setSalesReportDayOfEvent(diffInDays + 1);

        // Get sales for the 1st date range (out of 4)
        setSalesReportStatus('oneDayCurrentYear');
        setTimeout(() => {
          getSalesByKiosk({
            startDate: currentYearReportDate,
            endDate: currentYearReportDate
          });
        }, 0);
      }
    } catch (err) {
      setErrorMessage('Unable to determine the date range for the report');
    }
  };

  const onSaveClick = () => {
    const updatedConfiguration = getUpdatedResourceWithFormValues(
      selectedConfiguration as Configuration,
      configurationForm
    );
    // Specify which keys to update to avoid immutable keys like createdAt and updatedAt
    const payload: UpdateConfigurationPayload = {
      id: updatedConfiguration.id,
      configuration: updatedConfiguration.configuration,
      ticketing: updatedConfiguration.ticketing
    };
    updateConfiguration(payload);
    setResetForm(configurationForm);
    setHasUnsavedChanges(false);
  };

  const renderFieldLabel = (label: string, readOnly: boolean = false, indented: boolean = false) => (
    <Typography
      className={classes.fieldLabel}
      sx={{
        color: readOnly ? 'grey' : 'black',
        paddingLeft: indented ? '16px' : '0px'
      }}
    >
      {label}
    </Typography>
  );

  return (
    <>
      {errorMessage && (
        <FormDialog
          cancelButtonAction={() => setErrorMessage('')}
          confirmButtonAction={() => setErrorMessage('')}
          confirmButtonText="Ok"
          open={Boolean(errorMessage)}
        >
          <Typography fontSize={24} maxWidth="524px">
            {errorMessage}
          </Typography>
        </FormDialog>
      )}
      <Grid>
        {/* Top section */}
        <FlexBox direction="column" padding="7 7 0" justify="between">
          {/* Page header */}
          <Box flexOverride="none">
            <Typography className={classes.settingsHeaderText}>Settings</Typography>
          </Box>
          {/* Clear cache button */}
          <FlexBox margin="5 0 0" align="center">
            {renderFieldLabel('Clear cached event data')}
            <Button
              customSize="small"
              isLoading={clientAction.pending}
              onClick={invalidateCache}
              sx={{ margin: '0px 10px' }}
            >
              Clear Cache
            </Button>
          </FlexBox>
          {/* Kiosk Sales Report (SFT only) */}
          {client.configuration.salesReportSettings && (
            <FlexBox direction="column" margin="5 0 0" justify="between">
              {renderFieldLabel('Kiosk Sales Report')}
              <FlexBox sx={{ margin: '24px 0 0' }}>
                <MuiBox>
                  <InputField
                    label="Date (MM-DD-YYYY)"
                    size="medium"
                    placeholder="MM-DD-YYYY"
                    value={salesReportDate}
                    sx={{ height: '45px' }}
                    onChange={(e) => onSalesReportDateInputChange(e)}
                    data-testid="kiosk-sales-report-date-input"
                  />
                </MuiBox>
                <Button
                  customSize="small"
                  disabled={!isSalesReportDateValid}
                  isLoading={settingsAction.pending || salesReportStatus !== ''}
                  onClick={onBuildReportClick}
                  className={classes.salesReportButton}
                  data-testid="build-report-button"
                >
                  Build Report
                </Button>
              </FlexBox>
            </FlexBox>
          )}
          {/* Configuration selection */}
          <FlexBox fullWidth margin="5 0 5" align="center">
            {renderFieldLabel('Configuration')}
            <Box width={20} />
            <SelectDropdown
              items={getSortedList(configurations, 'name')}
              itemToString={(item: Configuration) => item && item.name}
              selectedItem={selectedConfiguration as Configuration}
              onChange={(item: Configuration) => setConfigurationId(item.id)}
              radius="round"
              width="262px"
              sx={{
                height: '45px',
                '& .MuiSelect-select': {
                  color: selectedConfiguration ? '#000' : '#9E9E9E'
                }
              }}
              data-testid="configuration-selector"
            />
          </FlexBox>
        </FlexBox>
        {/* Bottom section (settings and save button) */}
        <FlexBox padding="0 7 7" noWrap direction="column">
          {/* Container of settings */}
          <FlexBox align="center" margin="0 0 5" direction="row">
            <Box
              raised
              width="100%"
              minHeight="572px"
              flexOverride="none"
              padding="24px"
              sx={{ borderRadius: '4px', overflowY: 'auto' }}
            >
              <Grid container spacing={2} alignItems="center" rowGap={2}>
                {/* Primary Color */}
                <Grid item xs={12} sm={4}>
                  {renderFieldLabel('Primary Color', configurationForm.primaryColor.readOnly)}
                </Grid>
                <Grid item xs={12} sm={8}>
                  <ColorPicker
                    value={configurationForm.primaryColor.value}
                    onChange={(color) => onValueChange(color, 'primaryColor', setConfigurationForm)}
                    disabled={configurationForm.primaryColor.readOnly}
                  />
                </Grid>
                {/* Primary Font */}
                <Grid item xs={12} sm={4}>
                  {renderFieldLabel('Primary Font', configurationForm.primaryFont.readOnly)}
                </Grid>
                <Grid item xs={12} sm={8}>
                  <SelectDropdown
                    items={filteredFonts}
                    selectedItem={filteredFonts.find((f) => f.code === configurationForm.primaryFont.value) || null}
                    itemToString={(item) => (item ? item.displayName : '')}
                    onChange={(item) => onValueChange(item.code, 'primaryFont', setConfigurationForm)}
                    placeholder={'Select Primary Font'}
                    radius="round"
                    width="262px"
                    sx={{
                      height: '45px',
                      '& .MuiSelect-select': {
                        color: configurationForm.primaryFont.value ? '#000' : '#9E9E9E'
                      }
                    }}
                    data-testid={'primary-font-selector'}
                  />
                </Grid>
                {/* Donation Prompt */}
                <Grid item xs={12} sm={4}>
                  {renderFieldLabel('Donation Prompt', configurationForm.donationPopup.readOnly)}
                </Grid>
                <Grid item xs={12} sm={8}>
                  <Switch
                    checked={configurationForm.donationPopup.value === true}
                    checkedLabel="Enabled"
                    disabled={configurationForm.donationPopup.readOnly}
                    uncheckedLabel="Disabled"
                    containerStyle={{ width: '130px' }}
                    onChange={(e) => onValueChange(e.target.checked, 'donationPopup', setConfigurationForm)}
                    data-testid="donation-roundup-switch"
                  />
                </Grid>
                {/* Customer ID */}
                <Grid item xs={12}>
                  {renderFieldLabel('Customer ID')}
                </Grid>
                {/* Customer ID - Lookup */}
                <Grid item xs={12} sm={4}>
                  {renderFieldLabel('Lookup', configurationForm.lookup.readOnly, true)}
                </Grid>
                <Grid item xs={12} sm={8}>
                  <Switch
                    checked={configurationForm.lookup.value === true}
                    checkedLabel="Enabled"
                    uncheckedLabel="Disabled"
                    disabled={configurationForm.lookup.readOnly}
                    containerStyle={{ width: '130px' }}
                    onChange={(e) => onValueChange(e.target.checked, 'lookup', setConfigurationForm)}
                    data-testid="customer-lookup-switch"
                  />
                </Grid>
                {/* Customer ID - Creation */}
                <Grid item xs={12} sm={4}>
                  {renderFieldLabel('Creation', configurationForm.creation.readOnly, true)}
                </Grid>
                <Grid item xs={12} sm={8}>
                  <Switch
                    checked={configurationForm.creation.value === true}
                    checkedLabel="Enabled"
                    uncheckedLabel="Disabled"
                    disabled={configurationForm.creation.readOnly}
                    containerStyle={{ width: '130px' }}
                    onChange={(e) => onValueChange(e.target.checked, 'creation', setConfigurationForm)}
                    data-testid="customer-creation-switch"
                  />
                </Grid>
                {/* Survey */}
                <Grid item xs={12} sm={4}>
                  {renderFieldLabel('Survey', configurationForm.surveyPage.readOnly)}
                </Grid>
                <Grid item xs={12} sm={8}>
                  <Switch
                    checked={configurationForm.surveyPage.value === true}
                    checkedLabel="Enabled"
                    disabled={configurationForm.surveyPage.readOnly}
                    uncheckedLabel="Disabled"
                    containerStyle={{ width: '130px' }}
                    onChange={(e) => onValueChange(e.target.checked, 'surveyPage', setConfigurationForm)}
                    data-testid="survey-switch"
                  />
                </Grid>
              </Grid>
            </Box>
          </FlexBox>
          {/* Save button */}
          <FlexBox sx={{ justifyContent: 'flex-end', gap: '10px' }}>
            <Button
              customSize="small"
              width={144}
              onClick={() => setConfigurationForm(resetForm)}
              disabled={isDisabled}
            >
              Reset
            </Button>
            <Button
              customSize="small"
              width={144}
              disabled={hasInitialValues || !selectedConfiguration || !isSavable}
              onClick={onSaveClick}
            >
              Save
            </Button>
          </FlexBox>
        </FlexBox>
      </Grid>
    </>
  );
};

export default SettingsView;
